// base
import React from 'react';

// styles
import { StyledEnvironmentCard } from '../style';
import { EnvironmentRow } from '../../style';
import { StyledWeatherGraphCardTitle, StyledWeatherGraphWrapper } from './style';

// components
import { LazyImage } from 'components';

// constants
import { DEFAULT_TIME_FORMAT } from 'constants/const';
import { environmentIcon } from 'constants/images';

// hooks
import { IEnvironmentGraphItem } from '../../hooks/useEnvironment';

// libraries
import { Chart as ChartJS, ChartData, ChartOptions, Plugin } from 'chart.js';
import { Chart } from 'react-chartjs-2';
import {
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  DoughnutController,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Title,
  Tooltip
} from 'chart.js';
import { hexToRgb } from 'utils';
import { isEmpty } from 'lodash-es';
import dayjs from 'dayjs';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  ArcElement,
  LineController,
  BarController,
  BarElement,
  LineElement,
  Title,
  Tooltip,
  DoughnutController,
  Legend
);

interface WeatherGraphCardProps {
  item: IEnvironmentGraphItem;
}

export const WeatherGraphCard: React.FC<WeatherGraphCardProps> = ({ item }) => {
  const title = item.title;
  const titleIcon = item.titleIcon;
  let bgColor = item.bgColor;
  const isGradient = item.type === 'line';
  const datasets = item.datasets;
  const labels = item.labels.map((label) => dayjs(label).format(DEFAULT_TIME_FORMAT));
  const headers = item.headers;
  const type = item.type;
  const isShowLegends = false;
  const isStacked = false;
  const isShowGridLine = true;

  if (isEmpty(datasets)) {
    return <></>;
  }

  let colorArray: string[] | undefined;

  if (Array.isArray(bgColor)) {
    colorArray = bgColor;
    bgColor = colorArray[0];
  }

  const hexColor = hexToRgb(bgColor);

  // 차트용 데이터셋 가공
  const formatData = datasets.map((dataset, index) => {
    // wind 차트라면 windSpeed와 windDirection을 모두 추출
    if (title === 'Wind') {
      const windSpeedData = dataset.map((item) => Number(item.windSpeed));
      const windDirectionData = dataset.map((item) => Number(item.windDirection));

      return {
        type: type,
        label: headers[index] ?? '-',
        hidden: false,
        borderColor: colorArray ? colorArray[index] : bgColor,
        backgroundColor: (context: { chart: ChartJS }) => {
          const ctx = context.chart.ctx;
          if (!isGradient) return bgColor;
          const gradient = ctx.createLinearGradient(0, 0, 0, 100);
          if (colorArray) {
            const newColor = hexToRgb(colorArray[index]);
            gradient.addColorStop(0, `rgba(${newColor.r},${newColor.g},${newColor.b},0.5)`);
            gradient.addColorStop(1, `rgba(${newColor.r},${newColor.g},${newColor.b},0.05)`);
          } else {
            gradient.addColorStop(0, `rgba(${hexColor.r},${hexColor.g},${hexColor.b},0.5)`);
            gradient.addColorStop(1, `rgba(${hexColor.r},${hexColor.g},${hexColor.b},0.05)`);
          }
          return gradient;
        },
        borderWidth: 2,
        data: windSpeedData,
        // custom property로 windDirection 데이터도 같이 저장
        windDirections: windDirectionData,
        fill: true,
        barPercentage: 0.5,
        categoryPercentage: 1
      };
    }

    // WIND 차트가 아니라면
    else {
      return {
        type: type,
        label: headers[index] ?? '-',
        hidden: false,
        borderColor: colorArray ? colorArray[index] : bgColor,
        backgroundColor: (context: { chart: ChartJS }) => {
          const ctx = context.chart.ctx;
          if (!isGradient) return bgColor;
          const gradient = ctx.createLinearGradient(0, 0, 0, 100);
          if (colorArray) {
            const newColor = hexToRgb(colorArray[index]);
            gradient.addColorStop(0, `rgba(${newColor.r},${newColor.g},${newColor.b},0.5)`);
            gradient.addColorStop(1, `rgba(${newColor.r},${newColor.g},${newColor.b},0.05)`);
          } else {
            gradient.addColorStop(0, `rgba(${hexColor.r},${hexColor.g},${hexColor.b},0.5)`);
            gradient.addColorStop(1, `rgba(${hexColor.r},${hexColor.g},${hexColor.b},0.05)`);
          }
          return gradient;
        },
        borderWidth: 2,
        data: dataset?.map((item) => Number(item)),
        fill: true,
        barPercentage: 0.5,
        categoryPercentage: 1
      };
    }
  });

  const initData: ChartData<any> = {
    labels,
    datasets: formatData
  };

  let drawTextPlugin: Plugin<'line'> | Plugin<'bar'> | any = {};

  const maxDataValue = Math.max(...(initData.datasets[0].data as number[]));
  let stepSize = 1;
  if (maxDataValue >= 5000) {
    stepSize = 1000;
  } else if (maxDataValue >= 500) {
    stepSize = 100;
  } else if (maxDataValue >= 50) {
    stepSize = 10;
  }

  let options: ChartOptions = {
    responsive: false,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: isShowLegends || headers.length > 1,
        labels: {
          font: {
            size: 9.5
          } as any
        }
      }
    },
    scales: {
      x: {
        stacked: isStacked,
        display: isShowGridLine,
        grid: {
          display: true,
          drawOnChartArea: false,
          drawTicks: false
        },
        ticks: {
          color: '#222222E5',
          font: {
            size: 12,
            family: 'Poppins'
          },
          autoSkip: true,
          maxTicksLimit: 4 // 라벨 최대 표시 개수 예: 4
        }
      },
      y: {
        stacked: isStacked,
        display: isShowGridLine,
        // borderDash: [10, 10],
        // borderDashOffset: 1.1,
        position: 'right',
        grid: {
          color: 'rgba(34, 34, 34, 0.3)',
          display: isShowGridLine,
          drawOnChartArea: isShowGridLine,
          drawTicks: false,
          lineWidth: 0.5
        },
        ticks: {
          color: '#222222E5',
          font: {
            size: 12,
            family: 'Poppins'
          },
          stepSize: stepSize
        },
        border: {
          dash: [2, 4]
        }
      }
    },
    layout: {
      padding:
        title === 'Wind'
          ? {
              left: 0,
              right: 0,
              top: 50,
              bottom: 0
            }
          : undefined
    }
  };

  const iconPlugin: Plugin<'line'> = {
    id: 'iconPlugin',
    afterDatasetsDraw: function (chart) {
      if (title !== 'Wind') {
        return;
      }

      const iconImage = new Image();
      const ctx = chart.ctx;
      iconImage.src = environmentIcon.windDirection;

      chart.data.datasets.forEach((dataset: any, i) => {
        const meta = chart.getDatasetMeta(i);
        const windDirections = dataset.windDirections;

        if (!meta.hidden && windDirections) {
          meta.data.forEach((element: any, index: number) => {
            // console.log('windDirections[index]', windDirections[index]);

            const position = element.tooltipPosition();
            ctx.save();
            ctx.translate(position.x - 5, position.y - 26);
            ctx.rotate((windDirections[index] * Math.PI) / 180);
            ctx.drawImage(iconImage, 0, 0, 10, 10);
            ctx.restore();
          });
        }
      });
    }
  };

  return (
    <StyledEnvironmentCard style={{ flex: 1 }}>
      <StyledWeatherGraphWrapper>
        <EnvironmentRow style={{ alignItems: 'center', alignSelf: 'flex-start', gap: 0 }}>
          <StyledWeatherGraphCardTitle>{title}</StyledWeatherGraphCardTitle>
          <LazyImage src={titleIcon} alt="Flag" />
        </EnvironmentRow>
        <Chart
          className="chart"
          data={initData}
          options={options}
          plugins={[drawTextPlugin, iconPlugin]}
          type={type as any}
        />
      </StyledWeatherGraphWrapper>
    </StyledEnvironmentCard>
  );
};
